import React, { useEffect, useState, useMemo, useCallback } from "react";
import DataTable from "react-data-table-component";
import Header from "./Header";
import Footer from "./Footer";
import API_BASE_URL from "../Config";
import "./styles/Model.css";
import "../styles/global.css";


/* ────────── SVG Icon System ────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "block", flexShrink: 0 }}
  >
    {d}
  </svg>
);

const SearchIcon       = (p) => <Icon {...p} d={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} />;
const PlusIcon         = (p) => <Icon {...p} d={<><path d="M12 5v14M5 12h14" /></>} />;
const EditIcon         = (p) => <Icon {...p} d={<><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" /></>} />;
const TrashIcon        = (p) => <Icon {...p} d={<><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>} />;
const CloseIcon        = (p) => <Icon {...p} d={<><path d="M18 6 6 18M6 6l12 12" /></>} />;
const BoxIcon          = (p) => <Icon {...p} d={<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></>} />;
const UploadIcon       = (p) => <Icon {...p} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>} />;
const ChevronLeftIcon  = (p) => <Icon {...p} d={<><path d="m15 18-6-6 6-6" /></>} />;
const ChevronRightIcon = (p) => <Icon {...p} d={<><path d="m9 18 6-6-6-6" /></>} />;
const InfoIcon         = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>} />;

/* ────────── iOS Toggle Switch ────────── */
const IosSwitch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`ios-switch ${checked ? "is-on" : "is-off"}`}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
  >
    <span className="ios-switch__thumb" />
  </button>
);

/* ────────── Confirm Dialog ────────── */
const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="cdlg-overlay" role="dialog" aria-modal="true" aria-labelledby="cdlg-title">
      <div className="cdlg-box">
        <div className={`cdlg-icon-wrap cdlg-icon-${confirmVariant}`}>
          {confirmVariant === "danger" ? (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
            </svg>
          ) : (
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h3 id="cdlg-title" className="cdlg-title">{title}</h3>
        <p className="cdlg-message">{message}</p>
        <div className="cdlg-actions">
          <button type="button" className="cdlg-btn cdlg-btn-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className={`cdlg-btn cdlg-btn-confirm cdlg-btn-${confirmVariant}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ────────── Custom Pagination ────────── */
const CustomPagination = ({ currentPage, totalPages, onPageChange, perPage, totalRows, onPerPageChange }) => {
  const pages = useMemo(() => {
    const arr = [];
    const delta = 2;
    const left = currentPage - delta;
    const right = currentPage + delta + 1;
    let l;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i < right)) arr.push(i);
    }
    const withEllipsis = [];
    arr.forEach((i) => {
      if (l) {
        if (i - l === 2) withEllipsis.push(l + 1);
        else if (i - l > 2) withEllipsis.push("...");
      }
      withEllipsis.push(i);
      l = i;
    });
    return withEllipsis;
  }, [currentPage, totalPages]);

  const start = totalRows === 0 ? 0 : Math.min((currentPage - 1) * perPage + 1, totalRows);
  const end   = Math.min(currentPage * perPage, totalRows);

  return (
    <div className="md-pagination">
      <div className="md-pagination__info">
        <span>
          Showing <b>{start}</b>–<b>{end}</b> of <b>{totalRows}</b>
        </span>
        <select
          className="md-pagination__perpage"
          value={perPage}
          onChange={(e) => onPerPageChange(Number(e.target.value))}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n} / page</option>
          ))}
        </select>
      </div>
      <div className="md-pagination__pages">
        <button
          className="md-pagination__btn"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon size={16} />
        </button>
        {pages.map((p, i) =>
          p === "..." ? (
            <span key={`e-${i}`} className="md-pagination__ellipsis">…</span>
          ) : (
            <button
              key={p}
              className={`md-pagination__btn ${p === currentPage ? "is-active" : ""}`}
              onClick={() => onPageChange(p)}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          className="md-pagination__btn"
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => onPageChange(currentPage + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon size={16} />
        </button>
      </div>
    </div>
  );
};

/* ────────── Main Component ────────── */
const Model = () => {
  const [loading, setLoading]               = useState(false);
  const [models, setModels]                 = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [filterText, setFilterText]         = useState("");

  const [name, setName]       = useState("");
  const [status, setStatus]   = useState("Active");
  const [message, setMessage] = useState("");

  const [messagealert, setMessageAlert] = useState("");
  const [messageType, setMessageType]   = useState("");

  const [isEditMode, setIsEditMode] = useState(false);
  const [editSlug, setEditSlug]     = useState(null);

  const [image, setImage]     = useState(null);
  const [preview, setPreview] = useState(null);

  const [showModal, setShowModal] = useState(false);

  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  const [switchingSlug, setSwitchingSlug] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: "", message: "", confirmLabel: "Confirm",
    confirmVariant: "danger", onConfirm: null,
  });

  const token = localStorage.getItem("authToken");

  const showConfirm = ({ title, message, confirmLabel, confirmVariant = "danger", onConfirm }) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, confirmVariant, onConfirm });
  };
  const closeConfirm = () => setConfirmDialog((p) => ({ ...p, open: false, onConfirm: null }));

  const flashMessage = (msg, type = "success") => {
    setMessageAlert(msg);
    setMessageType(type);
    setTimeout(() => { setMessageAlert(""); setMessageType(""); }, 4000);
  };

  /* ── Image preview ── */
  useEffect(() => {
    if (!image) { setPreview(null); return; }
    const objectUrl = URL.createObjectURL(image);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [image]);

  /* ── Fetch Models ── */
  const fetchModels = useCallback((p = 1, size = perPage) => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/models?page=${p}&per_page=${size}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status && Array.isArray(data.data)) {
          setModels(data.data);
          setFilteredModels(data.data);
          setTotalRows(data.pagination.total);
          setPage(data.pagination.current_page);
        } else {
          console.error("Unexpected response format:", data);
        }
      })
      .catch((err) => console.error("Error fetching models:", err))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchModels(1, perPage); }, []);

  /* ── Pagination handlers ── */
  const handlePageChange = (p) => {
    setPage(p);
    fetchModels(p, perPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1);
    fetchModels(1, newPerPage);
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / perPage));

  /* ── Search filter ── */
  useEffect(() => {
    const result = models.filter((m) =>
      m.name?.toLowerCase().includes(filterText.toLowerCase()) ||
      m.slug?.toLowerCase().includes(filterText.toLowerCase()) ||
      m.status?.toLowerCase().includes(filterText.toLowerCase())
    );
    setFilteredModels(result);
  }, [filterText, models]);

  /* ── Status toggle ── */
  const handleStatusChange = (slug, currentStatus) => {
    if (switchingSlug === slug) return;
    const newStatus = currentStatus === "Active" ? "InActive" : "Active";
    showConfirm({
      title: "Change Status",
      message: `Are you sure you want to set this model to "${newStatus}"?`,
      confirmLabel: "Yes, Change",
      confirmVariant: "warning",
      onConfirm: () => {
        closeConfirm();
        setSwitchingSlug(slug);
        fetch(`${API_BASE_URL}api/models/status/${slug}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage(`Status updated to ${newStatus} successfully!`, "success");
              fetchModels(page, perPage);
            } else {
              flashMessage("Failed to update status.", "danger");
            }
          })
          .catch(() => flashMessage("Error while updating status.", "danger"))
          .finally(() => setSwitchingSlug(null));
      },
    });
  };

  /* ── Add / Update Model ── */
  const handleSaveModel = (e) => {
    e.preventDefault();
    setMessage("");

    const formData = new FormData();
    formData.append("name", name);
    formData.append("is_home", 1);
    formData.append("status", status);
    if (image) formData.append("image", image);

    const url = isEditMode
      ? `${API_BASE_URL}api/models/${editSlug}`
      : `${API_BASE_URL}api/models`;

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === true) {
          flashMessage(isEditMode ? "Model updated successfully!" : "Model added successfully!", "success");
          setName("");
          setStatus("Active");
          setImage(null);
          setPreview(null);
          setIsEditMode(false);
          setShowModal(false);
          fetchModels(page, perPage);
        } else {
          flashMessage("Failed to save model.", "danger");
        }
      })
      .catch(() => flashMessage("Something went wrong!", "danger"));
  };

  /* ── Edit Model ── */
  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditSlug(row.slug);
    setName(row.name);
    setStatus(row.status);
    setPreview(row.image ? `${API_BASE_URL}public/model/${row.image}` : `${API_BASE_URL}public/noimage.jpg`);
    setImage(null);
    setShowModal(true);
  };

  /* ── Delete Model ── */
  const handleDelete = (slug) => {
    showConfirm({
      title: "Delete Model",
      message: "Are you sure you want to delete this model? This action cannot be undone.",
      confirmLabel: "Yes, Delete",
      confirmVariant: "danger",
      onConfirm: () => {
        closeConfirm();
        fetch(`${API_BASE_URL}api/models/destroy/${slug}`, {
          method: "GET",
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.status) {
              flashMessage("Model deleted successfully!", "success");
              fetchModels(page, perPage);
            } else {
              flashMessage("Failed to delete model.", "danger");
            }
          })
          .catch(() => flashMessage("Error deleting model.", "danger"));
      },
    });
  };

  /* ── Open Add Modal ── */
  const openAddModal = () => {
    setIsEditMode(false);
    setEditSlug(null);
    setName("");
    setStatus("Active");
    setImage(null);
    setPreview(null);
    setShowModal(true);
  };

  /* ── DataTable custom styles ── */
  const tableStyles = useMemo(() => ({
    table: { style: { background: "transparent" } },
    headRow: {
      style: {
        background: "#f0fbf6",
        borderTopLeftRadius: "0px",
        borderTopRightRadius: "0px",
        borderBottom: "1px solid #d6efe2",
        minHeight: "52px",
      },
    },
    headCells: {
      style: {
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "#5b6b66",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        fontSize: "14px",
        color: "#1f2a37",
        borderBottom: "1px solid #eef4f1 !important",
        transition: "background 0.18s ease",
        fontFamily: "'Inter', sans-serif",
      },
      highlightOnHoverStyle: {
        background: "#f6fcf9",
        borderBottomColor: "#e3f0e9",
        outline: "none",
      },
      stripedStyle: { background: "#fafefb" },
    },
    cells: { style: { paddingLeft: "16px", paddingRight: "16px" } },
    pagination: { style: { display: "none" } },
  }), []);

  /* ── Table Columns ── */
  const columns = useMemo(() => [
    {
      name: "ID",
      selector: (row) => row.id,
      cell: (row) => <span className="md-id-pill">#{row.id}</span>,
      sortable: true,
      width: "90px",
    },
    {
      name: "Image",
      cell: (row) => (
        <div className="md-img-wrap">
          <img
            src={row.image ? `${API_BASE_URL}public/model/${row.image}` : `${API_BASE_URL}public/noimage.jpg`}
            alt={row.name}
          />
        </div>
      ),
      width: "80px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => <span className="md-cell-name">{row.name}</span>,
      sortable: true,
      grow: 2,
    },
    {
      name: "Slug",
      selector: (row) => row.slug,
      cell: (row) => <span className="md-slug-pill">{row.slug}</span>,
      grow: 2,
    },
    {
      name: "Status",
      cell: (row) => (
        <div className="md-status-wrap">
          <IosSwitch
            checked={row.status === "Active"}
            onChange={() => handleStatusChange(row.slug, row.status)}
            disabled={switchingSlug === row.slug}
          />
          <span className={`md-status-label ${row.status === "Active" ? "is-active" : "is-inactive"}`}>
            {row.status === "Active" ? "Active" : "Inactive"}
          </span>
        </div>
      ),
      width: "150px",
    },
    {
      name: "Created At",
      selector: (row) => row.created_at,
      cell: (row) => (
        <span className="md-cell-muted">
          {row.created_at
            ? new Date(row.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
            : "—"}
        </span>
      ),
      sortable: true,
      width: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="md-row-actions">
          <button className="md-ico-btn edit" onClick={() => handleEdit(row)} title="Edit Model" type="button">
            <EditIcon size={15} />
          </button>
          <button className="md-ico-btn del" onClick={() => handleDelete(row.slug)} title="Delete Model" type="button">
            <TrashIcon size={15} />
          </button>
        </div>
      ),
      width: "110px",
      ignoreRowClick: true,
      allowOverflow: true,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [switchingSlug]);

  /* ────────── Render ────────── */
  return (
    <>
      <div id="layout-wrapper" className="md-page">
        <Header />

        {loading && (
          <div className="md-overlay"><div className="md-spinner" /></div>
        )}

        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">

              {/* Page Header */}
              <div className="md-header">
                <div className="md-header-left">
                  <h1>Models</h1>
                  <p>Manage your product models — add, edit, or organise them.</p>
                </div>
                <button type="button" className="md-add-btn" onClick={openAddModal}>
                  <PlusIcon size={18} />
                  Add Model
                </button>
              </div>

              {/* Card */}
              <div className="md-card">

                {/* Toolbar */}
                <div className="md-card-toolbar">
                  <div className="md-search">
                    <SearchIcon size={17} />
                    <input
                      type="text"
                      placeholder="Search by name, slug or status…"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                  <div className="md-stats">
                    <span className="md-stat-chip">
                      <BoxIcon size={14} />
                      Total: <b>{totalRows}</b>
                    </span>
                  </div>
                </div>

                {/* Alert */}
                {messagealert && (
                  <div className={`md-alert ${messageType}`} role="alert">
                    <InfoIcon size={16} />
                    {messagealert}
                    <button type="button" className="close" onClick={() => setMessageAlert("")}>×</button>
                  </div>
                )}

                {/* DataTable — pagination hidden via customStyles, custom pagination below */}
                <DataTable
                  columns={columns}
                  data={filteredModels}
                  customStyles={tableStyles}
                  highlightOnHover
                  striped
                  responsive
                  pagination={false}
                  noDataComponent={
                    <div className="md-empty">
                      <BoxIcon size={44} />
                      <h4>No models found</h4>
                      <p>Try adjusting your search or add a new model.</p>
                    </div>
                  }
                />

                {/* Custom Pagination */}
                {totalRows > 0 && (
                  <CustomPagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    perPage={perPage}
                    totalRows={totalRows}
                    onPerPageChange={handlePerPageChange}
                  />
                )}

              </div>
            </div>
          </div>

          {/* Add / Edit Modal */}
          {showModal && (
            <div
              className="md-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
              aria-modal="true"
              role="dialog"
              aria-labelledby="mdModalTitle"
            >
              <div className="md-modal">
                <div className="md-modal__header">
                  <div className="md-modal__header-left">
                    <div className="md-modal__icon">
                      <BoxIcon size={20} />
                    </div>
                    <div>
                      <p className="md-modal__title" id="mdModalTitle">
                        {isEditMode ? "Edit Model" : "Add New Model"}
                      </p>
                      <p className="md-modal__subtitle">
                        {isEditMode ? "Update the details of this model" : "Fill in the details to create a new model"}
                      </p>
                    </div>
                  </div>
                  <button type="button" className="md-modal__close" onClick={() => setShowModal(false)} aria-label="Close modal">
                    <CloseIcon size={16} />
                  </button>
                </div>

                <form id="addModelForm" onSubmit={handleSaveModel}>
                  <div className="md-modal__body">
                    {/* Name */}
                    <div className="md-form-group">
                      <label className="md-form-label" htmlFor="mdName">Name <span>*</span></label>
                      <input
                        type="text" id="mdName" className="md-form-control"
                        placeholder="Model name" value={name}
                        onChange={(e) => setName(e.target.value)}
                        required autoFocus
                      />
                    </div>

                    {/* Image upload */}
                    <div className="md-form-group">
                      <label className="md-form-label" htmlFor="mdImage">Image</label>
                      <div className="md-file-upload">
                        <input
                          type="file" id="mdImage" accept="image/*"
                          onChange={(e) => setImage(e.target.files[0])}
                        />
                        <div className="md-file-upload__inner">
                          <UploadIcon size={22} />
                          <p className="md-file-upload__text"><b>Click to upload</b> or drag & drop</p>
                          <p className="md-file-upload__text" style={{ fontSize: 12 }}>PNG, JPG, WEBP up to 5MB</p>
                        </div>
                      </div>
                      {preview && (
                        <img src={preview} alt="Preview" className="md-preview-thumb" />
                      )}
                    </div>

                    {/* Status */}
                    <div className="md-form-group">
                      <label className="md-form-label" htmlFor="mdStatus">Status</label>
                      <select
                        id="mdStatus" className="md-form-select"
                        value={status} onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="Active">Active</option>
                        <option value="InActive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="md-modal__footer">
                    <button type="button" className="md-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="md-btn-submit">
                      {isEditMode ? <><EditIcon size={15} /> Update Model</> : <><PlusIcon size={15} /> Add Model</>}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Custom Confirm Dialog */}
          <ConfirmDialog
            open={confirmDialog.open}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmLabel={confirmDialog.confirmLabel}
            confirmVariant={confirmDialog.confirmVariant}
            onConfirm={confirmDialog.onConfirm}
            onCancel={closeConfirm}
          />

          <Footer />
        </div>
      </div>
    </>
  );
};

export default Model;